import { tr } from '@/lib/i18n-core';
import { ChevronLeft, ChevronRight, SlidersHorizontal } from 'lucide-react';
import { cn, todayIso } from '@/lib/utils';
import { Button, DatePicker, Popover, PopoverContent, PopoverTrigger, Select, Switch } from '@/components/ui/primitives';
import type { CalendarProvider, CalendarView } from './calendar-types';
import { shiftIsoDate } from './calendar-time';
import { ProviderPicker } from './ProviderPicker';

export interface CalendarToolbarProps {
  branches: { id: string; name: string }[];
  branchId: string;
  onBranch: (id: string) => void;
  departments: { id: string; name: string }[] | undefined;
  departmentId: string;
  onDepartment: (id: string) => void;
  date: string;
  onDate: (date: string) => void;
  view: CalendarView;
  onView: (view: CalendarView) => void;
  providers: CalendarProvider[];
  selected: string[];
  onProviders: (ids: string[]) => void;
  onResetProviders: () => void;
  compact: boolean;
  onCompact: (compact: boolean) => void;
  connected: boolean;
  phone: boolean;
}

function LiveDot({ connected }: { connected: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground" title={connected ? tr('opd', 'live_updates_on') : tr('opd', 'live_updates_off')}>
      <span className={cn('h-2 w-2 rounded-full', connected ? 'bg-success' : 'bg-muted-foreground/40')} aria-hidden />
      {connected ? tr('opd', 'live') : tr('opd', 'offline')}
    </span>
  );
}

export function CalendarToolbar(props: CalendarToolbarProps) {
  const { date, onDate, view, onView, phone, connected } = props;
  const step = view === 'week' ? 7 : 1;

  const scopeControls = (
    <>
      {props.branches.length > 1 && (
        <Select value={props.branchId} onChange={(event) => props.onBranch(event.target.value)} className="w-44" aria-label={tr('opd', 'branch')}>
          {props.branches.map((branch) => (
            <option key={branch.id} value={branch.id}>
              {branch.name}
            </option>
          ))}
        </Select>
      )}
      {props.departments && props.departments.length > 0 && (
        <Select value={props.departmentId} onChange={(event) => props.onDepartment(event.target.value)} className="w-44" aria-label={tr('opd', 'department')}>
          <option value="">{tr('opd', 'all_departments')}</option>
          {props.departments.map((department) => (
            <option key={department.id} value={department.id}>
              {department.name}
            </option>
          ))}
        </Select>
      )}
    </>
  );

  const viewControls = (
    <>
      <div className="inline-flex rounded-md border bg-background p-0.5" role="group" aria-label={tr('opd', 'view')}>
        {(['day', 'week'] as CalendarView[]).map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={view === option}
            onClick={() => onView(option)}
            className={cn(
              'rounded px-2.5 py-1 text-xs font-medium transition-colors focus-ring',
              view === option ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {option === 'day' ? tr('opd', 'day') : tr('opd', 'week')}
          </button>
        ))}
      </div>
      <ProviderPicker providers={props.providers} selected={props.selected} onChange={props.onProviders} onReset={props.onResetProviders} />
      <Switch checked={props.compact} onCheckedChange={props.onCompact} label={tr('opd', 'compact')} />
    </>
  );

  return (
    <div className="mb-3 flex flex-wrap items-center gap-2">
      <div className="inline-flex items-center gap-1">
        <Button variant="outline" size="icon" className="h-9 w-9" aria-label={tr('opd', 'previous')} onClick={() => onDate(shiftIsoDate(date, -step))}>
          <ChevronLeft className="h-4 w-4 rtl:rotate-180" aria-hidden />
        </Button>
        <Button variant="outline" size="sm" className="h-9" onClick={() => onDate(todayIso())}>
          {tr('opd', 'calendar_today')}
        </Button>
        <Button variant="outline" size="icon" className="h-9 w-9" aria-label={tr('opd', 'next')} onClick={() => onDate(shiftIsoDate(date, step))}>
          <ChevronRight className="h-4 w-4 rtl:rotate-180" aria-hidden />
        </Button>
      </div>
      <DatePicker value={date} onChange={(next) => next && onDate(next)} className="w-40" required />

      {phone ? (
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="icon" className="h-9 w-9" aria-label={tr('opd', 'filters')}>
              <SlidersHorizontal className="h-4 w-4" aria-hidden />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="flex w-64 flex-col gap-2">
            {scopeControls}
            {viewControls}
            <LiveDot connected={connected} />
          </PopoverContent>
        </Popover>
      ) : (
        <>
          {scopeControls}
          {viewControls}
          <div className="ms-auto">
            <LiveDot connected={connected} />
          </div>
        </>
      )}
    </div>
  );
}
